<template>
  <article class="activity-radar">
    <header class="activity-radar__header">
      <div>
        <span class="activity-radar__eyebrow">{{ headerEyebrow }}</span>
        <h2>{{ headerTitle }}</h2>
        <p>{{ headerDescription }}</p>
      </div>
      <div v-if="profile" class="activity-radar__meta">
        <span><v-icon small>mdi-calendar-month-outline</v-icon>{{ rangeLabel }}</span>
        <span><v-icon small>mdi-clock-outline</v-icon>{{ formatHours(profile.workSummary && profile.workSummary.hours) }}</span>
        <span><v-icon small>mdi-calendar-account-outline</v-icon>{{ Number(profile.workSummary && profile.workSummary.shifts) || 0 }} Schichten</span>
      </div>
    </header>

    <div v-if="loading" class="activity-radar__loading">
      <v-progress-circular indeterminate color="#38bdf8" size="34" width="3" />
      <span>Arbeitsprofil wird geladen …</span>
    </div>

    <v-alert v-else-if="error" outlined color="error" class="ma-0">
      {{ error }}
    </v-alert>

    <div v-else-if="!profile" class="activity-radar__empty">
      <v-icon size="44" color="#64748b">mdi-radar</v-icon>
      <strong>{{ emptyTitle }}</strong>
      <span>{{ emptyDescription }}</span>
    </div>

    <template v-else>
      <div v-if="profile.centerLabels && profile.centerLabels.length" class="activity-radar__centers">
        <v-icon small color="#7dd3fc">mdi-map-marker-multiple-outline</v-icon>
        {{ profile.centerLabels.join(" · ") }}
      </div>

      <div class="activity-radar__main">
        <div class="activity-radar__chart">
          <trainer-radar-plot
            :height="radarHeight"
            :labels="axes.map((axis) => axis.label)"
            :values="radarValues"
            :max="radarMax"
            color="#38bdf8"
            :aria-label="radarAriaLabel" />
        </div>

        <div class="activity-radar__axes">
          <button
            v-for="axis in axes"
            :key="axis.key"
            type="button"
            :disabled="!breakdownFor(axis.key)"
            :class="{
              'activity-radar__axis--clickable': breakdownFor(axis.key),
              'activity-radar__axis--active': activeBreakdown && activeBreakdown.metricKey === axis.key,
            }"
            @click="selectBreakdown(axis.key)">
            <span class="activity-radar__axis-icon">
              <v-icon small :color="axis.color">{{ axis.icon }}</v-icon>
            </span>
            <span class="activity-radar__axis-copy">
              <strong>{{ axis.label }}</strong>
              <small>{{ axisHint(axis) }}</small>
              <i><b :style="{ width: `${Math.min(axisValue(axis), 100)}%`, backgroundColor: axis.color }"></b></i>
            </span>
            <span class="activity-radar__axis-value">{{ formatRate(axisValue(axis)) }}</span>
            <v-icon v-if="breakdownFor(axis.key)" small color="#64748b">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>

      <section v-if="availableBreakdowns.length" class="activity-radar__details">
        <div class="activity-radar__detail-tabs" role="tablist" aria-label="Arbeitsprofil aufschlüsseln">
          <button
            v-for="breakdown in availableBreakdowns"
            :key="breakdown.metricKey"
            type="button"
            role="tab"
            :aria-selected="activeBreakdown && activeBreakdown.metricKey === breakdown.metricKey ? 'true' : 'false'"
            :class="{ 'activity-radar__detail-tab--active': activeBreakdown && activeBreakdown.metricKey === breakdown.metricKey }"
            @click="activeBreakdownKey = breakdown.metricKey">
            <v-icon small :color="breakdown.color">{{ breakdown.icon }}</v-icon>
            {{ breakdown.label }}
            <span>{{ breakdown.summary.count }}</span>
          </button>
        </div>

        <div v-if="activeBreakdown" class="activity-radar__detail-panel">
          <div class="activity-radar__detail-heading">
            <div>
              <span>{{ activeBreakdown.label }} IM DETAIL</span>
              <h3>{{ activeBreakdown.question }}</h3>
            </div>
            <div>
              <strong>{{ formatDuration(activeBreakdown.summary.minutes) }}</strong>
              <small>{{ formatCount(activeBreakdown.summary.count, activeBreakdown.singular, activeBreakdown.plural) }}</small>
            </div>
          </div>

          <trainer-radar-plot
            v-if="breakdownUsesRadar"
            :height="breakdownHeight"
            :labels="breakdownLabels"
            :values="breakdownValues"
            :max="breakdownMax"
            :color="activeBreakdown.color"
            :aria-label="`Spinnennetz für ${activeBreakdown.label}`" />

          <v-alert v-else dense outlined :color="activeBreakdown.color" class="mb-4">
            Für ein lesbares Detail-Spinnennetz braucht es mindestens drei verschiedene Einträge. Die vorhandenen Werte stehen vollständig unten.
          </v-alert>

          <div class="activity-radar__list">
            <div v-for="item in activeBreakdown.items" :key="item.key">
              <span class="activity-radar__list-dot" :style="{ backgroundColor: activeBreakdown.color }"></span>
              <span>
                <strong :title="item.label">{{ item.label }}</strong>
                <small>{{ formatDuration(item.minutes) }} · {{ formatCount(item.count, "Eintrag", "Einträge") }}</small>
              </span>
              <span>
                <strong>{{ formatRate(item.shareRate) }}</strong>
                <small>der {{ activeBreakdown.timeLabel }}</small>
              </span>
            </div>
          </div>
        </div>
      </section>
    </template>
  </article>
</template>

<script>
import TrainerRadarPlot from "./TrainerRadarPlot.vue"

export default {
  name: "TrainerActivityRadar",
  components: { TrainerRadarPlot },
  props: {
    profile: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    rangeLabel: { type: String, default: "" },
    variant: { type: String, default: "self", validator: (value) => ["self", "manager"].includes(value) },
    profileName: { type: String, default: "" },
  },
  data() {
    return {
      activeBreakdownKey: "amtliRate",
      axes: [
        { key: "appointmentRate", label: "Termine", icon: "mdi-calendar-clock-outline", color: "#4f9cf9", hint: "gebuchte Kundentermine" },
        { key: "trainerpointRate", label: "Trainerpoint", icon: "mdi-dumbbell", color: "#22d3ee", hint: "Arbeit im Trainerpoint" },
        { key: "thekeRate", label: "Theke", icon: "mdi-desk", color: "#f59e0b", hint: "Aufgabe mit Titel Theke" },
        { key: "amtliRate", label: "Ämtli", icon: "mdi-clipboard-check-outline", color: "#a78bfa", hint: "eingeplante Ämtli" },
        { key: "taskRate", label: "Aufgaben", icon: "mdi-format-list-checks", color: "#f472b6", hint: "weitere Arbeitsaufträge" },
        { key: "whiteSpaceRate", label: "Weisse Fläche", icon: "mdi-circle-outline", color: "#94a3b8", hint: "Netto-Schichtzeit ohne Kalendereintrag" },
      ],
      breakdownDefinitions: [
        {
          metricKey: "appointmentRate",
          field: "appointmentBreakdown",
          summaryField: "appointmentSummary",
          label: "Termine",
          icon: "mdi-calendar-clock-outline",
          color: "#4f9cf9",
          question: "Welche Terminarten hattest du?",
          managerQuestion: "Welche Terminarten wurden erfasst?",
          timeLabel: "Terminzeit",
          singular: "Termin",
          plural: "Termine",
        },
        {
          metricKey: "amtliRate",
          field: "amtliBreakdown",
          summaryField: "amtliSummary",
          label: "Ämtli",
          icon: "mdi-clipboard-check-outline",
          color: "#a78bfa",
          question: "Welche Ämtli hast du übernommen?",
          managerQuestion: "Welche Ämtli wurden übernommen?",
          timeLabel: "Ämtlizeit",
          singular: "Ämtli",
          plural: "Ämtli",
        },
        {
          metricKey: "taskRate",
          field: "taskBreakdown",
          summaryField: "taskSummary",
          label: "Aufgaben",
          icon: "mdi-format-list-checks",
          color: "#f472b6",
          question: "Welche Aufgaben hast du übernommen?",
          managerQuestion: "Welche Aufgaben wurden übernommen?",
          timeLabel: "Aufgabenzeit",
          singular: "Aufgabe",
          plural: "Aufgaben",
        },
      ],
    }
  },
  computed: {
    isManagerView() {
      return this.variant === "manager"
    },
    displayName() {
      return this.profileName || this.profile?.label || "Mitarbeiter"
    },
    headerEyebrow() {
      return this.isManagerView ? "ARBEITSPROFIL" : "MEIN ARBEITSPROFIL"
    },
    headerTitle() {
      return this.isManagerView ? `${this.displayName}: Tätigkeiten im Überblick` : "Deine Tätigkeiten im Überblick"
    },
    headerDescription() {
      return this.isManagerView
        ? "So setzt sich die eingeplante Zeit aus Terminen, Trainerpoint, Theke, Ämtli und Aufgaben zusammen."
        : "So setzt sich deine eingeplante Zeit aus Terminen, Trainerpoint, Theke, Ämtli und Aufgaben zusammen."
    },
    emptyTitle() {
      return this.isManagerView ? "Noch kein Arbeitsprofil für diese Person" : "Noch kein Arbeitsprofil für diesen Monat"
    },
    emptyDescription() {
      return this.isManagerView
        ? "Sobald Schichten und Kalendereinträge vorhanden sind, erscheint hier das Spinnennetz."
        : "Sobald Schichten und Kalendereinträge vorhanden sind, erscheint hier dein Spinnennetz."
    },
    radarAriaLabel() {
      return this.isManagerView
        ? `Spinnennetz der Tätigkeiten von ${this.displayName} im ausgewählten Monat`
        : "Spinnennetz deiner Tätigkeiten im ausgewählten Monat"
    },
    availableBreakdowns() {
      return this.breakdownDefinitions
        .map((definition) => {
          const rawSummary = this.profile?.[definition.summaryField] || {}
          return {
            ...definition,
            question: this.isManagerView ? definition.managerQuestion : definition.question,
            summary: {
              count: Math.max(0, Number(rawSummary.count || 0)),
              minutes: Math.max(0, Number(rawSummary.minutes || 0)),
            },
            items: this.normalizeItems(this.profile?.[definition.field]),
          }
        })
        .filter((definition) => definition.items.length > 0)
    },
    activeBreakdown() {
      return this.availableBreakdowns.find((entry) => entry.metricKey === this.activeBreakdownKey) || this.availableBreakdowns[0] || null
    },
    radarValues() {
      return this.axes.map((axis) => this.axisValue(axis))
    },
    radarMax() {
      return this.chartMaximum(this.axes.map((axis) => this.axisValue(axis)))
    },
    radarHeight() {
      return this.$vuetify.breakpoint.xsOnly ? 360 : 470
    },
    breakdownItems() {
      if (!this.activeBreakdown) return []
      if (this.activeBreakdown.items.length <= 6) return this.activeBreakdown.items
      const primary = this.activeBreakdown.items.slice(0, 5)
      const rest = this.activeBreakdown.items.slice(5)
      const additional = rest.reduce(
        (summary, item) => ({
          ...summary,
          count: summary.count + item.count,
          minutes: summary.minutes + item.minutes,
          shareRate: summary.shareRate + item.shareRate,
        }),
        { key: "__additional__", label: `Weitere (${rest.length})`, count: 0, minutes: 0, shareRate: 0 },
      )
      return [...primary, additional]
    },
    breakdownUsesRadar() {
      return this.breakdownItems.length >= 3
    },
    breakdownValues() {
      return this.breakdownItems.map((item) => item.shareRate)
    },
    breakdownLabels() {
      const maximum = this.$vuetify.breakpoint.xsOnly ? 13 : 21
      return this.breakdownItems.map((item) => {
        const label = String(item.label || "")
        return label.length > maximum ? `${label.slice(0, maximum - 1)}…` : label
      })
    },
    breakdownMax() {
      return this.chartMaximum(this.breakdownItems.map((item) => item.shareRate))
    },
    breakdownHeight() {
      return this.$vuetify.breakpoint.xsOnly ? 330 : 390
    },
  },
  methods: {
    axisValue(axis) {
      return Math.max(0, Number(this.profile?.metrics?.[axis.key] || 0))
    },
    axisHint(axis) {
      const breakdown = this.breakdownFor(axis.key)
      if (!breakdown) return axis.hint
      return `${formatCountValue(breakdown.summary.count, breakdown.singular, breakdown.plural)} · Details öffnen`
    },
    breakdownFor(metricKey) {
      return this.availableBreakdowns.find((entry) => entry.metricKey === metricKey) || null
    },
    selectBreakdown(metricKey) {
      if (this.breakdownFor(metricKey)) this.activeBreakdownKey = metricKey
    },
    normalizeItems(items) {
      return (Array.isArray(items) ? items : [])
        .map((item, index) => ({
          key: String(item?.key || `entry-${index}`),
          label: String(item?.label || "Eintrag"),
          count: Math.max(0, Number(item?.count || 0)),
          minutes: Math.max(0, Number(item?.minutes || 0)),
          shareRate: Math.max(0, Number(item?.shareRate !== undefined ? item.shareRate : item?.taskShareRate || 0)),
        }))
        .filter((item) => item.minutes > 0)
        .sort((a, b) => b.minutes - a.minutes || a.label.localeCompare(b.label, "de"))
    },
    chartMaximum(values) {
      const peak = Math.max(0, ...values.map(Number).filter(Number.isFinite))
      if (!peak) return 10
      return Math.min(100, Math.max(10, Math.ceil((peak * 1.05) / 10) * 10))
    },
    formatRate(value) {
      return `${Number(value || 0).toFixed(1)}%`
    },
    formatHours(value) {
      const hours = Math.max(0, Number(value || 0))
      return `${hours.toLocaleString("de-CH", { maximumFractionDigits: 1 })} h`
    },
    formatDuration(value) {
      const minutesTotal = Math.max(0, Math.round(Number(value || 0)))
      const hours = Math.floor(minutesTotal / 60)
      const minutes = minutesTotal % 60
      if (!hours) return `${minutes} Min.`
      return minutes ? `${hours} h ${minutes} Min.` : `${hours} h`
    },
    formatCount(value, singular, plural) {
      return formatCountValue(value, singular, plural)
    },
  },
}

function formatCountValue(value, singular, plural) {
  const count = Math.max(0, Math.round(Number(value || 0)))
  return `${count} ${count === 1 ? singular : plural}`
}
</script>

<style scoped>
.activity-radar {
  overflow: hidden;
  color: #e5eef9;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 24px;
  box-shadow: 0 22px 58px rgba(2, 6, 23, 0.26);
}

.activity-radar__header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 26px 28px 18px;
}

.activity-radar__eyebrow,
.activity-radar__detail-heading > div:first-child > span {
  display: block;
  margin-bottom: 6px;
  color: #7dd3fc;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
}

.activity-radar h2,
.activity-radar h3,
.activity-radar p {
  margin: 0;
}

.activity-radar h2 {
  color: #fff;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
}

.activity-radar__header p {
  max-width: 680px;
  margin-top: 7px;
  color: #94a3b8;
  line-height: 1.55;
}

.activity-radar__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.activity-radar__meta span,
.activity-radar__centers {
  display: inline-flex;
  gap: 7px;
  align-items: center;
  padding: 7px 10px;
  color: #cbd5e1;
  font-size: 0.78rem;
  font-weight: 700;
  background: rgba(30, 41, 59, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
}

.activity-radar__centers {
  margin: 0 28px;
}

.activity-radar__loading,
.activity-radar__empty {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  min-height: 330px;
  padding: 30px;
  color: #94a3b8;
  text-align: center;
}

.activity-radar__empty strong {
  color: #e2e8f0;
  font-size: 1rem;
}

.activity-radar__empty span {
  max-width: 480px;
}

.activity-radar__main {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  gap: 18px;
  align-items: center;
  padding: 8px 24px 26px;
}

.activity-radar__chart {
  min-width: 0;
}

.activity-radar__axes {
  display: grid;
  gap: 9px;
}

.activity-radar__axes button {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto 18px;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  color: inherit;
  text-align: left;
  background: rgba(30, 41, 59, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 13px;
  transition: 160ms ease;
}

.activity-radar__axes button:disabled {
  cursor: default;
}

.activity-radar__axis--clickable:hover,
.activity-radar__axis--active {
  background: rgba(51, 65, 85, 0.82) !important;
  border-color: rgba(125, 211, 252, 0.28) !important;
  transform: translateX(2px);
}

.activity-radar__axis-icon {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  background: rgba(148, 163, 184, 0.1);
  border-radius: 10px;
}

.activity-radar__axis-copy,
.activity-radar__axis-copy strong,
.activity-radar__axis-copy small {
  display: block;
  min-width: 0;
}

.activity-radar__axis-copy strong {
  color: #f8fafc;
  font-size: 0.86rem;
}

.activity-radar__axis-copy small {
  overflow: hidden;
  margin-top: 1px;
  color: #94a3b8;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-radar__axis-copy i {
  display: block;
  height: 3px;
  margin-top: 6px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.14);
  border-radius: 999px;
}

.activity-radar__axis-copy b {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.activity-radar__axis-value {
  color: #fff;
  font-size: 0.82rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.activity-radar__details {
  padding: 22px 28px 28px;
  background: rgba(2, 6, 23, 0.3);
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.activity-radar__detail-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.activity-radar__detail-tabs button {
  display: inline-flex;
  gap: 7px;
  align-items: center;
  padding: 8px 12px;
  color: #cbd5e1;
  font-weight: 800;
  background: rgba(30, 41, 59, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 999px;
}

.activity-radar__detail-tabs button > span {
  display: grid;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  place-items: center;
  color: #94a3b8;
  font-size: 0.7rem;
  background: rgba(15, 23, 42, 0.8);
  border-radius: 999px;
}

.activity-radar__detail-tab--active {
  color: #fff !important;
  background: rgba(51, 65, 85, 0.9) !important;
  border-color: rgba(125, 211, 252, 0.34) !important;
}

.activity-radar__detail-panel {
  padding: 20px;
  background: rgba(15, 23, 42, 0.68);
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 18px;
}

.activity-radar__detail-heading {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.activity-radar__detail-heading h3 {
  color: #fff;
  font-size: 1.05rem;
}

.activity-radar__detail-heading > div:last-child {
  text-align: right;
}

.activity-radar__detail-heading > div:last-child strong,
.activity-radar__detail-heading > div:last-child small {
  display: block;
}

.activity-radar__detail-heading > div:last-child strong {
  color: #fff;
}

.activity-radar__detail-heading > div:last-child small {
  color: #94a3b8;
}

.activity-radar__list {
  display: grid;
  gap: 7px;
}

.activity-radar__list > div {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  gap: 11px;
  align-items: center;
  padding: 9px 11px;
  background: rgba(30, 41, 59, 0.55);
  border-radius: 11px;
}

.activity-radar__list-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.activity-radar__list span:nth-child(2),
.activity-radar__list span:nth-child(2) strong,
.activity-radar__list span:nth-child(2) small,
.activity-radar__list span:last-child strong,
.activity-radar__list span:last-child small {
  display: block;
  min-width: 0;
}

.activity-radar__list span:nth-child(2) strong {
  overflow: hidden;
  color: #f8fafc;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-radar__list small {
  color: #94a3b8;
  font-size: 0.7rem;
}

.activity-radar__list span:last-child {
  text-align: right;
}

.activity-radar__list span:last-child strong {
  color: #fff;
  font-size: 0.82rem;
}

@media (max-width: 900px) {
  .activity-radar__header,
  .activity-radar__detail-heading {
    flex-direction: column;
  }

  .activity-radar__meta {
    justify-content: flex-start;
  }

  .activity-radar__main {
    grid-template-columns: 1fr;
  }

  .activity-radar__detail-heading > div:last-child {
    text-align: left;
  }
}

@media (max-width: 600px) {
  .activity-radar {
    border-radius: 18px;
  }

  .activity-radar__header,
  .activity-radar__details {
    padding: 20px 16px;
  }

  .activity-radar__centers {
    margin: 0 16px;
  }

  .activity-radar__main {
    padding: 4px 12px 20px;
  }

  .activity-radar__axes button {
    grid-template-columns: 32px minmax(0, 1fr) auto 16px;
    padding: 9px;
  }

  .activity-radar__detail-panel {
    padding: 14px;
  }
}
</style>
